import React, {useMemo} from 'react';
import { 
    Box, 
    Typography, 
    Paper,
    Divider,
    Chip,
    Tooltip
} from '@mui/material';
import Grid from '@mui/material/Grid';
import CustomBox from 'components/CustomBox';
import {formatDateTime} from 'util/helper';
import Checkmark from 'assets/icons/main/check_mark_icon.svg';
import ActiveCheckmarkIcon from 'assets/icons/main/active_check_mark.svg';
import ActivePurchasedIcon from 'assets/icons/main/active_purchased_icon.svg';
import PurchasedIcon from 'assets/icons/main/purchase_btn_icon.svg';
import EmailIcon from 'assets/icons/main/email_icon.svg';
import Loader from 'components/Loader';
import { useQuery } from '@tanstack/react-query';
import * as http from 'util/elemental_axios_client';

const Profile = ({ profileId }) => {
    // Profile data query
    const { 
        data: profileData,
        isLoading: isProfileLoading,
        error: profileError 
    } = useQuery({
        queryKey: ['profile', profileId],
        queryFn: async () => {
            const response = await http.get(`/profiles/${profileId}`);
            return response;
        },
        enabled: !!profileId,
        staleTime: 5 * 60 * 1000,
    });

    // Profile audiences query
    const { 
        data: profileAudiences = [],
        isLoading: isAudiencesLoading,
        error: audiencesError 
    } = useQuery({
        queryKey: ['profile-audiences', profileId],
        queryFn: async () => {
            const response = await http.get(`/profiles/${profileId}/audiences`);
            return response;
        },
        enabled: !!profileId,
        staleTime: 5 * 60 * 1000,
    });

    // Profile logs query
    const { 
        data: userLogsData = [],
        isLoading: isLogsLoading,
        error: logsError 
    } = useQuery({
        queryKey: ['profile-logs', profileId],
        queryFn: async () => {
            const response = await http.get(`/profiles/${profileId}/logs`);
            return response.logs || [];
        },
        enabled: !!profileId,
        staleTime: 60 * 1000,
    });

    const isLoading = isProfileLoading || isAudiencesLoading || isLogsLoading;
    const hasError = profileError || audiencesError || logsError;

    const renderCheckmark = (id) => (
        id ? <img src={ActiveCheckmarkIcon} alt='checkmark_true' /> : <img src={Checkmark} alt='checkmark_false' />
    );

    const logsColumns = useMemo(
        () => [
            { 
                Header: 'Description', 
                sticky: 'left', 
                accessor: 'event_name', 
                Cell: ({row}) => <div>{row.original.event_name}</div>, 
                width: 200 
            },
            { 
                Header: 'Date', 
                accessor: 'date', 
                Cell: ({row}) => <div>{!row.original.date ? '-' : formatDateTime(row.original.date)}</div>, 
                width: 100 
            },
            { 
                Header: '', 
                sticky: 'right', 
                id: 'menu', 
                Cell: () => true, 
                width: 0
            }
        ],
        []
    );

    const renderLogs = () => (
        <Box p={1}>
            {/*<TableComponent columns={logsColumns} data={userLogsData} style={{maxHeight: '400px'}} filterBy='event_name' />*/}
        </Box>
    );

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <Loader style={{}} />
            </Box>
        );
    }

    if (hasError) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <Typography color="error">Error loading profile data</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%', p: 3 }}>
            {/* Profile Header */}
            <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="h5">
                        {profileData?.email || '-'}
                    </Typography>
                    {profileData?.is_email_suppressed && (
                        <Chip 
                            label="Suppressed"
                            size="small"
                            sx={{ 
                                bgcolor: 'error.main',
                                color: 'error.contrastText',
                                '& .MuiChip-label': {
                                    px: 1
                                }
                            }}
                        />
                    )}
                </Box>
                <Typography variant="caption" color="text.secondary">
                    Added {profileData?.created_at ? formatDateTime(profileData.created_at) : '-'}
                </Typography>
            </Box>

            {/* Main Content Area */}
            <Grid container spacing={3}>
                {/* Left Side - Profile Details */}
                <Grid item xs={12} md={8}>
                    <Paper sx={{ p: 3, bgcolor: 'grey.50' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {/* Activity Summary */}
                            <Box>
                                <Box sx={{ 
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(3, 1fr)',
                                    gap: 2
                                }}>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">
                                            Last Send
                                        </Typography>
                                        <Typography variant="body2">
                                            {profileData?.last_send_at ? formatDateTime(profileData.last_send_at) : 'Never'}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">
                                            Last Open
                                        </Typography>
                                        <Typography variant="body2">
                                            {profileData?.last_open_at ? formatDateTime(profileData.last_open_at) : 'Never'}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">
                                            Last Click
                                        </Typography>
                                        <Typography variant="body2">
                                            {profileData?.last_click_at ? formatDateTime(profileData.last_click_at) : 'Never'}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>

                            <Divider />

                            {/* Profile Properties */}
                            <Box>
                                <Typography variant="overline" color="text.secondary" sx={{ mb: 1 }}>
                                    Profile Properties
                                </Typography>
                                <Box sx={{ 
                                    maxHeight: '33vh',
                                    overflowY: 'auto',
                                    '&::-webkit-scrollbar': {
                                        width: '8px',
                                    },
                                    '&::-webkit-scrollbar-track': {
                                        background: 'transparent',
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                        background: 'rgba(0,0,0,0.2)',
                                        borderRadius: '4px',
                                    },
                                    '&::-webkit-scrollbar-thumb:hover': {
                                        background: 'rgba(0,0,0,0.3)',
                                    }
                                }}>
                                    <Box sx={{ 
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(2, 1fr)',
                                        gap: 1
                                    }}>
                                        {profileData?.props?.map((prop, id) => (
                                            <React.Fragment key={id}>
                                                <Typography 
                                                    variant="caption" 
                                                    color="text.secondary"
                                                    sx={{ 
                                                        py: 0.5,
                                                        px: 1,
                                                        '&:hover': {
                                                            bgcolor: 'action.hover'
                                                        }
                                                    }}
                                                >
                                                    {prop.profile_prop.name_friendly}
                                                </Typography>
                                                <Typography 
                                                    variant="body2"
                                                    sx={{ 
                                                        py: 0.5,
                                                        px: 1,
                                                        '&:hover': {
                                                            bgcolor: 'action.hover'
                                                        }
                                                    }}
                                                >
                                                    {prop.value || '-'}
                                                </Typography>
                                            </React.Fragment>
                                        ))}
                                    </Box>
                                </Box>
                            </Box>

                            <Divider />

                            {/* Audiences */}
                            <Box>
                                <Typography variant="overline" color="text.secondary" sx={{ mb: 2 }}>
                                    Static Audiences
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {profileAudiences?.length > 0 ? (
                                        profileAudiences.map((audience, id) => (
                                            <Chip 
                                                key={id}
                                                label={audience.name}
                                                size="small"
                                                sx={{ 
                                                    bgcolor: 'background.paper',
                                                    '&:hover': {
                                                        bgcolor: 'action.hover'
                                                    }
                                                }}
                                            />
                                        ))
                                    ) : (
                                        <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                            No audiences
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    </Paper>
                </Grid>

                {/* Right Side - Empty for now */}
                <Grid item xs={12} md={4}>
                </Grid>
            </Grid>

            {/* Activity & Logs Section */}
            <Box sx={{ mt: 3 }}>
                <Grid container spacing={3}>
                    {/* Logs */}
                    <Grid item xs={12}>
                        <Paper sx={{ p: 3 }}>
                            <Typography variant="overline" color="text.secondary" sx={{ mb: 2 }}>
                                Recent Logs
                            </Typography>
                            {renderLogs()}
                        </Paper>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    );
};

export default Profile;
